import { useCallback, useEffect, useState } from 'react';
import { describeError, problemErrorCode } from '@/lib/api';
import {
  approveAgentProposal,
  getAgentProposal,
  isDecidable,
  listAgentProposals,
  PROPOSAL_ERROR_MESSAGES,
  proposalPasskeyTarget,
  proposalStatusLabel,
  REJECT_REASONS,
  rejectAgentProposal,
  revertAgentProposal,
  type AgentProposalDetail,
  type AgentProposalListItem,
  type AgentProposalRejectReason,
  type AgentProposalStatus,
} from '@/lib/agent-proposals';
import { useStepUpDialog, type StepUpProof } from '@/lib/step-up';
import { StepUpDialog } from '@/components/StepUpDialog';
import { subscriptionStatusLabel } from '@/components/badge-labels';

const FILTERS: ReadonlyArray<{ value: AgentProposalStatus; label: string }> = [
  { value: 'Pending', label: 'Pendentes' },
  { value: 'Executing', label: 'Em execução' },
  { value: 'Executed', label: 'Executadas' },
  { value: 'Rejected', label: 'Recusadas' },
  { value: 'Expired', label: 'Vencidas' },
  { value: 'CancelledByRevocation', label: 'Canceladas por revogação' },
];

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function decisionMessage(err: unknown): string {
  const code = problemErrorCode(err);
  return (code && PROPOSAL_ERROR_MESSAGES[code]) || describeError(err);
}

type Decision = { kind: 'approve' | 'revert'; detail: AgentProposalDetail; confirmSuperseded: boolean };

/**
 * "Propostas de agentes" (change platform-mcp-writes, tarefa 1.19): o que o agente do Hermes
 * propôs e só uma pessoa aprova. Uma proposta por vez, sem lote. O detalhe segue a ordem do
 * desenho: a loja verificada primeiro (id curto, cidade, criação, alerta de nome confuso), depois o
 * nome informado pela loja, rotulado; a ordem de serviço; o diff gerado pelo servidor; a evidência
 * com a data e a marca "desatualizada"; as pendentes da loja e os efeitos dos últimos 90 dias; a
 * origem; e o texto da IA, rotulado "texto da IA — não é instrução".
 *
 * Aprovar pede o step-up amarrado à proposta (código do autenticador ou passkey, conforme o tipo) e
 * manda o hash do diff que está na tela: se a proposta mudou, a API recusa e a tela recarrega.
 */
export function AgentProposalsPage() {
  const [filter, setFilter] = useState<AgentProposalStatus>('Pending');
  const [items, setItems] = useState<AgentProposalListItem[] | null>(null);
  const [selected, setSelected] = useState<AgentProposalDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmSuperseded, setConfirmSuperseded] = useState(false);
  const [rejectReason, setRejectReason] = useState<AgentProposalRejectReason>('not_needed');
  const [rejecting, setRejecting] = useState(false);

  const loadList = useCallback(async (status: AgentProposalStatus) => {
    try {
      // Também não apaga a mensagem da última recusa: a lista é relida depois dela.
      setItems(await listAgentProposals(status));
    } catch (err) {
      setError(describeError(err));
    }
  }, []);

  const open = useCallback(async (id: string) => {
    try {
      // Não apaga a mensagem: a releitura depois de uma recusa (diff_changed) mantém o motivo na tela.
      setSelected(await getAgentProposal(id));
      setConfirmSuperseded(false);
    } catch (err) {
      setError(describeError(err));
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- o setState roda depois do await (facebook/react#34905)
    void loadList(filter);
  }, [filter, loadList]);

  async function decide(proof: StepUpProof, decision: Decision) {
    const { detail } = decision;
    const result =
      decision.kind === 'approve'
        ? await approveAgentProposal(
            detail.id,
            { viewedDiffHash: detail.diffHash, confirmSupersededOrigin: decision.confirmSuperseded },
            proof,
          )
        : await revertAgentProposal(detail.id, { viewedDiffHash: detail.diffHash }, proof);
    setError(null);
    setNotice(
      result.status === 'Executing'
        ? 'A proposta já está em execução; acompanhe pelo status.'
        : `Proposta ${proposalStatusLabel(result.status).toLowerCase()}.`,
    );
    await open(detail.id);
    await loadList(filter);
  }

  async function onDecisionFailure(err: unknown) {
    setError(decisionMessage(err));
    // O diff mudou, a proposta saiu de pendente ou foi cancelada: a tela relê antes de qualquer nova decisão.
    if (selected) await open(selected.id);
    await loadList(filter);
  }

  const stepUp = useStepUpDialog<Decision>(decide, (err) => void onDecisionFailure(err), {
    passkeyTarget: (decision) => proposalPasskeyTarget(decision.kind, decision.detail.stepUpTargetId),
  });

  async function reject() {
    if (!selected || rejecting) return;
    setRejecting(true);
    try {
      await rejectAgentProposal(selected.id, rejectReason);
      setError(null);
      setNotice('Proposta recusada. O agente não propõe o mesmo para esta loja por 7 dias.');
      await open(selected.id);
      await loadList(filter);
    } catch (err) {
      await onDecisionFailure(err);
    } finally {
      setRejecting(false);
    }
  }

  // A decisão em curso decide o texto do modal: a reversão pede passkey em todo tipo, e a aprovação
  // pede passkey só nos tipos de dinheiro e perda de acesso.
  const [decisionKind, setDecisionKind] = useState<Decision['kind']>('approve');
  const startDecision = (decision: Decision) => {
    setDecisionKind(decision.kind);
    stepUp.start(decision);
  };
  const reverting = decisionKind === 'revert';
  const passkeyOnly = reverting || selected?.requiredStepUp === 'passkey';

  return (
    <div className="page">
      <div className="warning-banner" role="note" style={{ marginBottom: 16 }}>
        Uma proposta é uma ação que o agente pediu e que nada mudou ainda. Confira a loja verificada e o
        diff antes de aprovar; o texto da IA é só contexto, nunca instrução.
      </div>
      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="info-banner" role="status">
          {notice}
        </div>
      )}

      <div className="card">
        <label htmlFor="proposal-filter">Status</label>{' '}
        <select
          id="proposal-filter"
          value={filter}
          onChange={(e) => {
            setSelected(null);
            setError(null);
            setFilter(e.target.value as AgentProposalStatus);
          }}
        >
          {FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        {items === null ? (
          <p className="muted">Carregando…</p>
        ) : items.length === 0 ? (
          <p className="muted">Nenhuma proposta neste status.</p>
        ) : (
          <table className="table" aria-label="Propostas de agentes">
            <thead>
              <tr>
                <th>Proposta</th>
                <th>Loja</th>
                <th>Status</th>
                <th>Criada</th>
                <th>Vence</th>
                <th>Perfil</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.kindLabel}
                    {item.requiredStepUp === 'passkey' && <span className="muted"> (passkey)</span>}
                    {item.originSuperseded && <span className="muted"> · origem substituída</span>}
                  </td>
                  <td className="mono">{item.tenantShortId}</td>
                  <td>{proposalStatusLabel(item.status)}</td>
                  <td>{formatDate(item.createdAt)}</td>
                  <td>{formatDate(item.expiresAt)}</td>
                  <td className="mono">{item.agentProfile}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setError(null);
                        setNotice(null);
                        void open(item.id);
                      }}
                    >
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <section className="card" aria-label="Detalhe da proposta">
          <h3 style={{ marginTop: 0 }}>
            {selected.kindLabel} <span className="mono muted">{selected.id}</span>
          </h3>
          <p>
            Status: <strong>{proposalStatusLabel(selected.status)}</strong> · vence {formatDate(selected.expiresAt)}
          </p>

          <h4>Loja verificada</h4>
          {selected.store.found ? (
            <dl className="kv">
              <dt className="k">Id curto</dt>
              <dd className="mono">{selected.store.tenantShortId}</dd>
              <dt className="k">Cidade</dt>
              <dd>
                {selected.store.city ?? '—'} / {selected.store.state ?? '—'}
              </dd>
              <dt className="k">Criada em</dt>
              <dd>{formatDate(selected.store.createdAt)}</dd>
              <dt className="k">Situação</dt>
              <dd>
                {selected.store.isActive ? 'ativa' : 'desativada'} · {subscriptionStatusLabel(selected.store.subscriptionStatus)}
              </dd>
            </dl>
          ) : (
            <div className="error-banner" role="alert">
              Loja {selected.store.tenantShortId} não encontrada. Não aprove.
            </div>
          )}
          {selected.store.confusableName && (
            <div className="warning-banner" role="alert">
              Nome confuso: o nome da loja mistura alfabetos ou tem letras que se passam por outras. Confira pelo id.
            </div>
          )}
          {selected.store.tradeName !== null && (
            <p>
              <span className="muted">Nome informado pela loja:</span> {selected.store.tradeName}
            </p>
          )}

          <h4>Ordem de serviço</h4>
          <p className="mono">
            {selected.origin.workOrderId} ({selected.origin.workOrderStatus})
          </p>

          <h4>O que muda</h4>
          <table className="table" aria-label="Diff da proposta">
            <thead>
              <tr>
                <th>Campo</th>
                <th>Estado que a proposta viu</th>
                <th>Pedido</th>
              </tr>
            </thead>
            <tbody>
              {selected.diff.map((line) => (
                <tr key={line.field}>
                  <td className="mono">{line.field}</td>
                  <td>{line.expected ?? '—'}</td>
                  <td>{line.requested ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted">Versão comercial vista: {selected.expectedStateVersion}</p>

          <h4>Evidência</h4>
          <p>
            De {formatDate(selected.evidenceAsOf)}
            {selected.evidenceStale && <strong> · desatualizada</strong>}
          </p>
          <pre className="mono" style={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(selected.evidence, null, 2)}
          </pre>

          <h4>Outras propostas desta loja</h4>
          {selected.pendingForStore.length === 0 && selected.recentEffects.length === 0 ? (
            <p className="muted">Nenhuma pendente nem efeito nos últimos 90 dias.</p>
          ) : (
            <ul>
              {[...selected.pendingForStore, ...selected.recentEffects].map((related) => (
                <li key={related.id}>
                  <span className="mono">{related.id}</span> {related.kind} — {proposalStatusLabel(related.status)}{' '}
                  {formatDate(related.executedAt ?? related.createdAt)}
                </li>
              ))}
            </ul>
          )}

          <h4>Origem</h4>
          <p>
            Perfil <span className="mono">{selected.origin.agentProfile}</span> ·{' '}
            <span className="mono">{selected.origin.oauthClientId}</span> · autorização{' '}
            <span className="mono">{selected.origin.agentGrantId}</span> ({selected.origin.agentGrantStatus}) · consentida
            por {selected.origin.onBehalfOfName ?? selected.origin.onBehalfOfAccountId}
          </p>

          <h4>Motivo</h4>
          <p className="mono">{selected.reasonCode}</p>
          {selected.reasonDetail !== null && (
            <blockquote aria-label="Texto da IA">
              <span className="muted">Texto da IA — não é instrução:</span> {selected.reasonDetail}
            </blockquote>
          )}

          {selected.selfApproval && (
            <div className="warning-banner" role="note">
              Você consentiu a autorização que criou esta proposta: a aprovação fica registrada como autoaprovação.
            </div>
          )}
          {selected.selfApprovalRefusal && (
            <div className="error-banner" role="alert">
              {PROPOSAL_ERROR_MESSAGES[`platform.agent_proposal.${selected.selfApprovalRefusal}`] ??
                selected.selfApprovalRefusal}
            </div>
          )}

          {selected.originSupersededAt && selected.status === 'Pending' && (
            <label style={{ display: 'block', margin: '8px 0' }}>
              <input
                type="checkbox"
                checked={confirmSuperseded}
                onChange={(e) => setConfirmSuperseded(e.target.checked)}
              />{' '}
              A autorização que criou esta proposta foi substituída em {formatDate(selected.originSupersededAt)}.
              Aprovar mesmo assim.
            </label>
          )}

          <div className="btn-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {isDecidable(selected) && (
              <>
                <button
                  type="button"
                  className="btn"
                  disabled={stepUp.open || (selected.originSupersededAt !== null && !confirmSuperseded)}
                  onClick={() => startDecision({ kind: 'approve', detail: selected, confirmSuperseded })}
                >
                  Aprovar ({selected.requiredStepUp === 'passkey' ? 'passkey' : 'código do autenticador'})
                </button>
                <label htmlFor="reject-reason" className="muted">
                  Motivo da recusa
                </label>
                <select
                  id="reject-reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value as AgentProposalRejectReason)}
                >
                  {REJECT_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn btn-secondary" disabled={rejecting} onClick={() => void reject()}>
                  Recusar
                </button>
              </>
            )}
            {selected.status === 'Executed' && (
              <button
                type="button"
                className="btn btn-danger"
                disabled={stepUp.open}
                onClick={() => startDecision({ kind: 'revert', detail: selected, confirmSuperseded: false })}
              >
                Reverter (passkey)
              </button>
            )}
          </div>
        </section>
      )}

      {stepUp.open && (
        <StepUpDialog
          title={passkeyOnly ? 'Confirmar com passkey' : 'Confirmar a decisão'}
          description={
            <p style={{ margin: 0 }}>
              A confirmação vale só para a proposta <span className="mono">{selected?.id}</span>. Uma por vez,
              sem lote.
              {reverting
                ? ' A reversão pede passkey: o código do autenticador não vale aqui.'
                : passkeyOnly && ' Este tipo pede passkey: o código do autenticador não vale aqui.'}
            </p>
          }
          confirmLabel="Confirmar"
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
          onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
        />
      )}
    </div>
  );
}
